import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { altioraToast, toastApiError } from "@/components/altiora/toast";
import {
  PageShell, PageHeader, Card, Section, Badge, Pill, EmptyState,
  PrimaryButton, GhostButton, DangerButton, Modal, Label, FormField,
  Input, Select, IconButton, SkeletonCard,
  NAVY, CHAMPAGNE, IVORY, SLATE,
} from "@/components/altiora/PremiumUI";
import {
  BellRing, Mail, Smartphone, MapPin, PlusCircle, Trash2,
  Sparkles, CheckCircle2,
} from "lucide-react";

const DEFAULT_RULE = {
  label: "Ma veille ALTIORA",
  lat: 45.5017, lng: -73.5673,
  radius_km: 10, types: [], cost: null, countries: [],
  min_relevance: 60, channels: ["in_app"],
};

/**
 * Alerts — Opportunity watch rules
 *
 * Design-System-native (Phase B) : PageShell + PageHeader + Card + Section
 * + Badge + Pill + Modal (destructive guard) + EmptyState + FormField + Input +
 * Select + altioraToast. 100 % preserving feature parity with the legacy version
 * (channels availability, rules CRUD, preview endpoint, RBAC).
 */
export default function Alerts() {
  const { lang } = useI18n();
  const T = (fr, en) => (lang === "fr" ? fr : en);

  const [rules, setRules]                  = useState([]);
  const [channelsAvail, setChannelsAvail]  = useState({ in_app: true, email: false, sms: false });
  const [types, setTypes]                  = useState([]);
  const [draft, setDraft]                  = useState(DEFAULT_RULE);
  const [preview, setPreview]              = useState(null);
  const [loading, setLoading]              = useState(true);
  const [busy, setBusy]                    = useState(false);
  const [pendingDelete, setPendingDelete]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, tt] = await Promise.all([
        api.get("/alerts/rules"),
        api.get("/geo-gps/types"),
      ]);
      setRules(r.data.items || []);
      setChannelsAvail(r.data.channels_available || {});
      setTypes(tt.data.types || []);
    } catch (e) { toastApiError(e, T("Chargement impossible", "Load failed")); }
    finally { setLoading(false); }
  }, [lang]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/alerts/rules", draft);
      altioraToast.success(T("Règle créée", "Rule created"));
      setDraft(DEFAULT_RULE);
      setPreview(null);
      await load();
    } catch (e) { toastApiError(e); }
    finally { setBusy(false); }
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/alerts/preview", draft);
      setPreview(data);
      altioraToast.info(T(`${data.matches} correspondances`,
                             `${data.matches} matches`));
    } catch (e) { toastApiError(e); }
    finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await api.delete(`/alerts/rules/${pendingDelete.rule_id}`);
      altioraToast.success(T("Règle supprimée", "Rule deleted"));
      setPendingDelete(null);
      await load();
    } catch (e) { toastApiError(e); }
    finally { setBusy(false); }
  };

  const toggle = (key, list, apply) =>
    apply(list.includes(key) ? list.filter((x) => x !== key) : [...list, key]);

  const channels = [
    { key: "in_app", label: "In-app",     icon: BellRing,   note: T("Toujours disponible.", "Always on.") },
    { key: "email",  label: "Email",      icon: Mail,       note: T("Ajoutez SENDGRID_API_KEY + SENDER_EMAIL.", "Add SENDGRID_API_KEY + SENDER_EMAIL.") },
    { key: "sms",    label: "SMS",        icon: Smartphone, note: T("Ajoutez TWILIO_ACCOUNT_SID/AUTH_TOKEN/FROM_NUMBER.", "Add TWILIO_ACCOUNT_SID/AUTH_TOKEN/FROM_NUMBER.") },
  ];

  return (
    <PageShell testid="alerts-root">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <PageHeader
          eyebrow={T("ALTIORA · Veille économique", "ALTIORA · Economic watch")}
          title={T("Alertes d'opportunités", "Opportunity Alerts")}
          subtitle={T(
            "ALTIORA agit comme un agent toujours actif — les opportunités viennent à vous.",
            "ALTIORA acts as an always-on agent — opportunities come to you."
          )}
          testid="alerts-header" />

        {/* ── Channels availability ─────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          {channels.map((ch) => {
            const on = channelsAvail[ch.key];
            const Ico = ch.icon;
            return (
              <Card key={ch.key} className="p-5" testid={`alerts-channel-${ch.key}`}>
                <div className="flex items-center justify-between">
                  <Ico className="h-4 w-4" strokeWidth={1.8}
                          style={{ color: on ? "#0B7A3B" : SLATE }} />
                  <Badge variant={on ? "green" : "neutral"} size="sm">
                    {on ? T("Actif", "Active") : T("En attente", "Awaiting key")}
                  </Badge>
                </div>
                <div className="mt-3 font-serif text-base tracking-tight"
                        style={{ color: NAVY }}>{ch.label}</div>
                <div className="mt-1 text-[12px]" style={{ color: SLATE }}>{ch.note}</div>
              </Card>
            );
          })}
        </div>

        {/* ── Create rule ───────────────────────────────────────── */}
        <Card className="p-6 md:p-8 mt-4">
          <Section title={T("Vos critères de veille", "Your watch criteria")}
                       icon={PlusCircle}
                       hint={T("Précisez la géographie, les types d'opportunités et les canaux d'envoi.",
                                  "Refine geography, opportunity types and delivery channels.")}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <FormField label={T("Libellé", "Label")} testid="alerts-ff-label">
                {(a) => <Input type="text" value={draft.label}
                                     onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                                     testid="alerts-label" {...a} />}
              </FormField>
              <FormField label={T("Rayon (km)", "Radius (km)")}>
                {(a) => <Input type="number" min={1} max={500} value={draft.radius_km}
                                     onChange={(e) => setDraft({ ...draft, radius_km: Number(e.target.value) })}
                                     testid="alerts-radius" {...a} />}
              </FormField>
              <FormField label="Latitude">
                {(a) => <Input type="number" step="0.0001" value={draft.lat}
                                     onChange={(e) => setDraft({ ...draft, lat: Number(e.target.value) })}
                                     testid="alerts-lat" {...a} />}
              </FormField>
              <FormField label="Longitude">
                {(a) => <Input type="number" step="0.0001" value={draft.lng}
                                     onChange={(e) => setDraft({ ...draft, lng: Number(e.target.value) })}
                                     testid="alerts-lng" {...a} />}
              </FormField>
              <FormField label={T("Pertinence minimale", "Minimum relevance")}>
                {(a) => <Input type="number" min={0} max={100} value={draft.min_relevance}
                                     onChange={(e) => setDraft({ ...draft, min_relevance: Number(e.target.value) })}
                                     testid="alerts-min-rel" {...a} />}
              </FormField>
              <FormField label={T("Coût", "Cost")}>
                {(a) => (
                  <Select value={draft.cost || ""}
                             onChange={(e) => setDraft({ ...draft, cost: e.target.value || null })}
                             testid="alerts-cost" {...a}>
                    <option value="">{T("Tous", "All")}</option>
                    <option value="free">{T("Gratuit", "Free")}</option>
                    <option value="paid">{T("Payant", "Paid")}</option>
                  </Select>
                )}
              </FormField>
            </div>

            <div className="mt-6">
              <Label>{T("Types d'opportunité", "Opportunity types")}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {types.map((tp) => {
                  const active = draft.types.includes(tp.key);
                  return (
                    <button key={tp.key} data-testid={`alerts-type-${tp.key}`}
                                onClick={() => toggle(tp.key, draft.types, (nv) => setDraft({ ...draft, types: nv }))}
                                className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full transition"
                                style={{
                                  border: `1px solid ${active ? NAVY : `${CHAMPAGNE}55`}`,
                                  background: active ? `${NAVY}15` : "#fff",
                                  color: active ? NAVY : SLATE,
                                  fontWeight: active ? 600 : 400,
                                }}>
                      {tp[`label_${lang}`]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6">
              <Label>{T("Canaux d'envoi", "Delivery channels")}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {channels.map((c) => {
                  const active = draft.channels.includes(c.key);
                  const avail = channelsAvail[c.key];
                  return (
                    <button key={c.key} data-testid={`alerts-ch-${c.key}`}
                                disabled={!avail}
                                onClick={() => toggle(c.key, draft.channels, (nv) => setDraft({ ...draft, channels: nv }))}
                                className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded transition disabled:opacity-40 disabled:cursor-not-allowed"
                                style={{
                                  background: active ? NAVY : "#fff",
                                  border: `1px solid ${active ? NAVY : `${CHAMPAGNE}55`}`,
                                  color: active ? "#fff" : NAVY,
                                }}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <GhostButton onClick={runPreview} disabled={busy} testid="alerts-preview">
                <Sparkles size={12} /> {T("Aperçu", "Preview")}
              </GhostButton>
              <PrimaryButton onClick={create} disabled={busy} testid="alerts-create">
                <PlusCircle size={12} /> {T("Créer la règle", "Create rule")}
              </PrimaryButton>
              {preview && (
                <Badge variant="green" icon={CheckCircle2}>
                  {preview.matches} {T("correspondances", "matches")}
                </Badge>
              )}
            </div>

            {preview?.items?.length > 0 && (
              <div className="mt-4 pt-4 border-t space-y-1.5"
                       style={{ borderColor: `${CHAMPAGNE}22` }}>
                {preview.items.slice(0, 5).map((it) => (
                  <div key={it.key} className="flex items-center justify-between text-[13px]">
                    <span style={{ color: NAVY }}>{it.title_fr}</span>
                    <span className="font-mono text-[10px]" style={{ color: SLATE }}>
                      {it.distance_km.toFixed(1)} km · rel {it.relevance_score}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </Card>

        {/* ── Existing rules ────────────────────────────────────── */}
        <div className="mt-6">
          <div className="font-serif text-xl tracking-tight mb-4" style={{ color: NAVY }}>
            {T("Mes règles actives", "My active rules")}
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[0, 1].map(i => <SkeletonCard key={i} lines={3} />)}
            </div>
          ) : rules.length === 0 ? (
            <Card className="p-4">
              <EmptyState icon={BellRing}
                             title={T("Aucune règle active", "No active rules")}
                             description={T(
                               "Créez votre première règle ci-dessus pour recevoir des opportunités.",
                               "Create your first rule above to start receiving opportunities."
                             )}
                             testid="alerts-empty" />
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {rules.map((r) => (
                <Card key={r.rule_id} className="p-5" testid="alerts-rule-item">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-serif text-base tracking-tight" style={{ color: NAVY }}>
                        {r.label}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                        <Pill><MapPin size={10} /> {r.radius_km} km</Pill>
                        <Pill tone="navy">rel ≥ {r.min_relevance}</Pill>
                        {(r.channels || []).map(c => <Pill key={c} tone="gold">{c}</Pill>)}
                      </div>
                      <div className="mt-2 text-[11px] font-mono" style={{ color: SLATE }}>
                        {(r.types && r.types.length)
                          ? r.types.join(", ")
                          : T("tous types", "all types")}
                      </div>
                      {(r.last_sent_opp_keys || []).length > 0 && (
                        <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-mono"
                                style={{ color: "#0B7A3B" }}>
                          <CheckCircle2 size={11} />
                          {r.last_sent_opp_keys.length} {T("envoyées", "sent")}
                        </div>
                      )}
                    </div>
                    <IconButton icon={Trash2} label={T("Supprimer", "Delete")}
                                    tone="ghost" size={30}
                                    onClick={() => setPendingDelete(r)} />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Suppress rule confirmation modal */}
      <Modal open={!!pendingDelete} onClose={() => !busy && setPendingDelete(null)}
                 size="sm" testid="alerts-delete-modal"
                 title={T("Supprimer cette règle ?", "Delete this rule?")}
                 description={pendingDelete
                   ? T(`« ${pendingDelete.label} » ne recevra plus d'alertes.`,
                       `"${pendingDelete.label}" will stop receiving alerts.`)
                   : ""}
                 footer={
                   <>
                     <GhostButton onClick={() => setPendingDelete(null)} disabled={busy}>
                       {T("Annuler", "Cancel")}
                     </GhostButton>
                     <DangerButton onClick={confirmDelete} disabled={busy}
                                         testid="alerts-delete-confirm">
                       {busy ? "…" : T("Supprimer", "Delete")}
                     </DangerButton>
                   </>
                 } />
    </PageShell>
  );
}
